import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Button,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Checkbox,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Pagination from '@mui/material/Pagination';
import { useService } from 'contexts/ServiceContextProvider';
import { useTranslation } from "react-i18next";
import "../../../css/components/Calendar/Holiday/HolidayModal.css";
import "../../../css/main.css";
import "../../../css/components/Calendar/VacationRequestModal.css";
import TinyCustomFallback from 'components/Custom/TinyCustomFallback';
import CreateHolidayForm from './HolidayForm';
import { useSnackbar } from 'contexts/SnackbarContextProvider';
import DeleteIcon from '@mui/icons-material/Delete';
import TourIcon from '@mui/icons-material/Tour';
import { DateTime } from 'luxon';

const FILTER_OPTIONS = {
    ALL: "ALL",
    BLACKLISTED: "BLACKLISTED",
    CUSTOM: "CUSTOM",
    COUNTRY: "COUNTRY",
};

const HolidayModal = ({ open, onClose, workspaceId }) => {
    const { t } = useTranslation();
    const { fetchWorkspaceHolidays, updateHolidayBlacklist, createHoliday, deleteHoliday } = useService();
    const { showSnackbar } = useSnackbar();
    const [holidays, setHolidays] = useState([]);
    const [filteredHolidays, setFilteredHolidays] = useState([]);
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(FILTER_OPTIONS.ALL);
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedToBlacklist, setSelectedToBlacklist] = useState([]);
    const [selectedToUnblacklist, setSelectedToUnblacklist] = useState([]);
    const [currentMonth,] = useState(DateTime.local().startOf('month'));

    const [currentPage, setCurrentPage] = useState(1);
    const holidaysPerPage = 5;

    const [isCreating, setIsCreating] = useState(false);


    const applyFilter = (filter, holidaysList) => {
        if (filter === FILTER_OPTIONS.ALL) {
            return holidaysList.filter(h => !h.blacklisted || h.source === "Custom");
        } else if (filter === FILTER_OPTIONS.BLACKLISTED) {
            return holidaysList.filter(h => h.blacklisted);
        } else if (filter === FILTER_OPTIONS.CUSTOM) {
            return holidaysList.filter(h => h.source === "Custom");
        } else {
            return holidaysList.filter(h => h.country === filter);
        }
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (open && workspaceId && !isCreating) {

            resetState();
            setLoading(true);
            fetchWorkspaceHolidays(workspaceId)
                .then((data) => {
                    const { holidays, blacklisted_holidays } = data;

                    if (!Array.isArray(blacklisted_holidays)) {
                        console.error("blacklisted_holidays should be an array of holiday objects.");
                        setLoading(false);
                        return;
                    }

                    const mergedHolidays = [
                        ...holidays.map(holiday => ({ ...holiday, blacklisted: false })),
                        ...blacklisted_holidays.map(holiday => ({ ...holiday, blacklisted: true })),
                    ];

                    // Filter out holidays before the current month, as there's nothing to do with past ones
                    const filteredMergedHolidays = mergedHolidays.filter(holiday => {
                        const holidayDate = DateTime.fromISO(holiday.date);
                        return holidayDate >= currentMonth.startOf('month');
                    });

                    setHolidays(filteredMergedHolidays);
                    setFilteredHolidays(applyFilter(selectedCountry, filteredMergedHolidays));

                    const uniqueCountries = [
                        ...new Set(filteredMergedHolidays
                            .map(h => h.country)
                            .filter(country => country)
                        )
                    ];
                    setCountries(uniqueCountries);

                    setLoading(false);
                })
                .catch((error) => {
                    console.error('Error fetching holidays:', error);
                    setLoading(false);
                });
        }
    }, [open, workspaceId]);


    const initialState = {
        holidays: [],
        filteredHolidays: [],
        countries: [],
        selectedCountry: FILTER_OPTIONS.ALL,
        loading: true,
        editMode: false,
        selectedToBlacklist: [],
        selectedToUnblacklist: [],
        currentPage: 1,
    };

    const resetState = () => {
        setHolidays(initialState.holidays);
        setFilteredHolidays(initialState.filteredHolidays);
        setCountries(initialState.countries);
        setSelectedCountry(initialState.selectedCountry);
        setLoading(initialState.loading);
        setEditMode(initialState.editMode);
        setSelectedToBlacklist(initialState.selectedToBlacklist);
        setSelectedToUnblacklist(initialState.selectedToUnblacklist);
        setCurrentPage(initialState.currentPage);
        setIsCreating(false);
    };

    const groupedHolidays = filteredHolidays.reduce((acc, holiday) => {
        if (!acc[holiday.date]) acc[holiday.date] = [];
        acc[holiday.date].push(holiday);
        return acc;
    }, {});

    const groupedDates = Object.entries(groupedHolidays);

    const indexOfLastGroup = currentPage * holidaysPerPage;
    const indexOfFirstGroup = indexOfLastGroup - holidaysPerPage;
    const currentGroups = groupedDates.slice(indexOfFirstGroup, indexOfLastGroup);

    const handlePageChange = (event, value) => setCurrentPage(value);

    const handleCountryChange = (e) => {
        const selected = e.target.value;
        setSelectedCountry(selected);

        const newFiltered = applyFilter(selected, holidays);
        setFilteredHolidays(newFiltered);

        setCurrentPage(1);
    };


    const handleToggleBlacklist = (holidayId, isCurrentlyBlacklisted) => {
        if (isCurrentlyBlacklisted) {
            setSelectedToUnblacklist(prev =>
                prev.includes(holidayId) ? prev.filter(id => id !== holidayId) : [...prev, holidayId]
            );

            setSelectedToBlacklist(prev => prev.filter(id => id !== holidayId));

            const updatedHolidays = holidays.map(holiday =>
                holiday.id === holidayId ? { ...holiday, blacklisted: false } : holiday
            );
            setHolidays(updatedHolidays);

            const updatedFilteredHolidays = filteredHolidays.map(holiday =>
                holiday.id === holidayId ? { ...holiday, blacklisted: false } : holiday
            );
            setFilteredHolidays(updatedFilteredHolidays);
        } else {
            setSelectedToBlacklist(prev =>
                prev.includes(holidayId) ? prev.filter(id => id !== holidayId) : [...prev, holidayId]
            );

            setSelectedToUnblacklist(prev => prev.filter(id => id !== holidayId));

            const updatedHolidays = holidays.map(holiday =>
                holiday.id === holidayId ? { ...holiday, blacklisted: true } : holiday
            );
            setHolidays(updatedHolidays);

            const updatedFilteredHolidays = filteredHolidays.map(holiday =>
                holiday.id === holidayId ? { ...holiday, blacklisted: true } : holiday
            );
            setFilteredHolidays(updatedFilteredHolidays);
        }

    };

    const handleSaveBlacklist = async () => {
        const payload = {
            add: selectedToBlacklist,
            remove: selectedToUnblacklist,
        };
        const response = await updateHolidayBlacklist(workspaceId, payload);
        if (response?.message === "Holiday blacklist updated successfully.") {
            showSnackbar(t("calendar.blacklist_updated_successfully"), 'success');
            setTimeout(() => {
                window.location.reload();
            }, 500); 
        } else {
            console.error("Error saving blacklist.");
            showSnackbar(t("calendar.blacklist_update_failed"), 'error');
        }
    };

    const handleDeleteCustomHoliday = async (holidayId) => {
        try {
            const response = await deleteHoliday(workspaceId, holidayId);
            if (response?.success) {
                showSnackbar(t("calendar.holiday_deleted_successfully"), 'success');
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            } else {
                showSnackbar(t("calendar.holiday_deletion_failed"), 'error');
            }

        } catch (error) {
            console.error("Error deleting holiday:", error);
            showSnackbar(t("calendar.holiday_deletion_failed"), 'error');
        }
    };


    const handleCreateHoliday = async (newHolidayData) => {
        try {
            const response = await createHoliday(workspaceId, newHolidayData);
            if (response?.success) {
                showSnackbar(t("calendar.holiday_created_successfully"), 'success');
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            } else {
                showSnackbar(t("calendar.holiday_creation_failed"), 'error');
            }

        } catch (error) {
            console.error("Error creating holiday:", error);
            showSnackbar(t("calendar.holiday_creation_failed"), 'error');
        }
    };


    return (
        <Modal open={open} onClose={onClose}>
            <Box className="holiday-modal-main-style">
                <Typography variant="h6" className="holiday-modal-title">
                    {editMode ? t("calendar.holiday_edit_mode") : t("calendar.holiday_workspace")}
                </Typography>

                {!isCreating ? (
                    loading ? (
                        <div style={{ display: "flex", justifyContent: "center", marginTop: "150px", marginBottom: "150px" }}>
                            <TinyCustomFallback />
                        </div>
                    ) : (
                        <>
                            {/* Filter Section */}
                            <FormControl fullWidth margin="normal" className="request-vacation-form" sx={{
                                '.MuiInputLabel-root': {
                                    color: 'white',
                                    backgroundColor: '#1C1C1C',
                                    paddingLeft: "2px",
                                    paddingRight: "2px"
                                },
                                '.MuiInputLabel-root.Mui-focused': {
                                    color: 'white',
                                },
                            }}>
                                <InputLabel id="country-select-label" >{t("calendar.holiday_country_filter")}</InputLabel>
                                <Select
                                    labelId="country-select-label"
                                    value={selectedCountry}
                                    onChange={handleCountryChange}
                                    MenuProps={{
                                        PaperProps: {
                                            className: "request-vacation-type-select",
                                        },
                                    }}
                                    sx={{
                                        color: 'white',
                                        '.MuiSvgIcon-root': { color: 'white' },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'white',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'white',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'white',
                                        },
                                    }}
                                >
                                    <MenuItem value={FILTER_OPTIONS.ALL}>
                                        {t("calendar.holiday_country_all")}
                                    </MenuItem>
                                    <MenuItem value={FILTER_OPTIONS.BLACKLISTED}>
                                        {t("calendar.holiday_blacklisted")}
                                    </MenuItem>
                                    <MenuItem value={FILTER_OPTIONS.CUSTOM}>
                                        {t("calendar.holiday_custom_country")}
                                    </MenuItem>
                                    {countries.map(country => (
                                        <MenuItem key={country} value={country}>
                                            {country}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Holiday List */}
                            <div className="holiday-list-container">
                                {currentGroups.length === 0 ? (
                                    <Typography variant="body1" sx={{ display: "flex", justifyContent: "center" }}>
                                        {t("calendar.no_holidays_found")}
                                    </Typography>
                                ) : (
                                    currentGroups.map(([date, holidaysOnDate]) => {
                                        const hasCustomHoliday = holidaysOnDate.some(holiday => holiday.source === "Custom");

                                        return (
                                            <Accordion key={date} className="holiday-accordion" sx={{ mb: 1 }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'lightblue', ml: 1 }} />} className="accordion-summary">
                                                    <Typography sx={{ color: hasCustomHoliday ? 'lightblue' : 'white' }}>
                                                        {`${date} - ${holidaysOnDate.length} ${t(
                                                            holidaysOnDate.length === 1
                                                                ? "calendar.holiday"
                                                                : "calendar.holidays"
                                                        )} ${hasCustomHoliday ? `[${t("calendar.custom")}]` : ""}`}
                                                    </Typography>
                                                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
                                                        {holidaysOnDate.map((h, index) => (
                                                            h.country ? (
                                                                <img
                                                                    key={h.id || index}
                                                                    src={`https://flagcdn.com/w80/${h.country.toLowerCase()}.png`}
                                                                    alt={`${h.country} Flag`}
                                                                    style={{ width: 20, height: 14, marginLeft: "4px" }}
                                                                />
                                                            ) : (
                                                                <TourIcon
                                                                    key={h.id || index}
                                                                    style={{ width: 20, height: 20, marginLeft: "4px", color: "yellow" }}
                                                                />
                                                            )
                                                        ))}
                                                    </div>
                                                </AccordionSummary>
                                                <AccordionDetails className="custom-accordion-details">
                                                    <ul>
                                                        {holidaysOnDate.map(holiday => (
                                                            <li key={holiday.id} className={editMode ? "holiday-item-edit" : "holiday-item"}>
                                                                {editMode && (
                                                                    <Checkbox
                                                                        checked={!holiday.blacklisted}
                                                                        onChange={() => handleToggleBlacklist(holiday.id, holiday.blacklisted)}
                                                                        color="primary"
                                                                        inputProps={{ 'aria-label': `Toggle blacklist for ${holiday.name}` }}
                                                                    />
                                                                )}

                                                                <span style={{
                                                                    color: holiday.source === "Custom"
                                                                        ? (holiday.blacklisted && selectedCountry !== "BLACKLISTED" ? 'red' : 'lightblue')
                                                                        : (holiday.blacklisted && selectedCountry === "BLACKLISTED" ? 'red' : 'white')
                                                                }} className={`holiday-text ${holiday.blacklisted ? "blacklisted" : ""}`}>
                                                                    {`${holiday.local_name} (${holiday.name}) - [${holiday.country || t("calendar.custom")}]`}

                                                                    {/* Conditional Rendering of Flag or TourIcon */}
                                                                    {holiday.country ? (
                                                                        <img
                                                                            src={`https://flagcdn.com/w80/${holiday.country.toLowerCase()}.png`}
                                                                            alt={`${holiday.country} Flag`}
                                                                            style={{ width: 20, height: 14, marginLeft: "4px" }}
                                                                        />
                                                                    ) : (
                                                                        <TourIcon
                                                                            style={{ width: 20, height: 20, marginLeft: "4px", color: "lightblue" }}
                                                                            aria-label="Custom Holiday Icon"
                                                                            title="Custom Holiday"
                                                                        />
                                                                    )}
                                                                </span>

                                                                {holiday.source === "Custom" && !editMode && (
                                                                    <Button
                                                                        className='delete-icon'
                                                                        onClick={(event) => handleDeleteCustomHoliday(holiday.id)}
                                                                    >
                                                                        <DeleteIcon />
                                                                    </Button>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>

                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    })
                                )
                                }
                            </div>

                            {/* Pagination */}
                            <Pagination
                                count={Math.ceil(groupedDates.length / holidaysPerPage)}
                                page={currentPage}
                                onChange={handlePageChange}
                                className="holiday-modal-pagination"
                            />



                            {/* Edit Mode Buttons */}
                            <Box display="flex" justifyContent="space-between" marginTop={2}>
                                {!editMode ? (
                                    <>
                                        <Button variant="contained" className='info-button-holiday' onClick={() => setEditMode(true)}>
                                            {t("calendar.holiday_edit")}
                                        </Button>
                                        <Button variant="contained" className='accept-button' onClick={() => { setIsCreating(true); setEditMode(false); }}>
                                            {t("calendar.holiday_create_button")}
                                        </Button>

                                    </>
                                ) : (
                                    <>
                                        <Button variant="contained" className='decline-button' onClick={() => setEditMode(false)}>
                                            {t("global.cancel")}
                                        </Button>
                                        <Button variant="contained" className='accept-button' onClick={handleSaveBlacklist}>
                                            {t("global.save")}
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </>
                    )
                ) : (

                    <CreateHolidayForm
                        handleCreateHoliday={handleCreateHoliday}
                        countries={countries}
                        onCloseCreateForm={() => setIsCreating(false)}
                        workspaceId={workspaceId}
                    />
                )}


            </Box>
        </Modal>
    );

};

export default HolidayModal;


